import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export type CareerOperationKey =
	| 'shifts'
	| 'rotations'
	| 'suspensions'
	| 'objectives'
	| 'cfu'
	| 'career'
	| 'registrations'
	| 'evaluations'
	| 'report'
	| 'generate_ds'
	| 'generate_allegato'
	| 'download_ds'
	| 'download_allegato'
	| 'send_alert';

export interface CareerOperationsParams extends ICellRendererParams {
	onOperation: (op: CareerOperationKey, career: any) => void;
}

interface OpButton {
	key: CareerOperationKey;
	icon: string;
	title: string;
	className: string;
	visible: (career: any) => boolean;
}

@Component({
	selector: 'core-career-operations-cell-renderer',
	template: `
		<div class="d-inline-flex align-items-center">
			@if (isGenerating) {
				<fa-icon
					icon="spinner"
					[spin]="true"
					class="text-warning me-50"
					[title]="'common_label_generating_in_progress' | translate"></fa-icon>
			}
			<div ngbDropdown container="body" placement="bottom-end bottom-start top-end top-start" class="d-inline-block">
				<button type="button" class="btn btn-sm btn-icon btn-flat-primary rounded-circle dropdown-toggle-hide-arrow" ngbDropdownToggle>
					<fa-icon icon="ellipsis-v"></fa-icon>
				</button>
				<div ngbDropdownMenu class="dropdown-menu-end">
					@for (b of buttons; track b) {
						<button ngbDropdownItem (click)="onClick(b.key)">
							<fa-icon [icon]="b.icon" class="me-50"></fa-icon>
							<span>{{ b.title | translate }}</span>
						</button>
					}
				</div>
			</div>
		</div>
	`,
	styles: [
		`
			.btn-icon {
				width: 32px;
				height: 32px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				padding: 0;
			}
			.me-50 {
				margin-right: 0.5rem;
			}
			::ng-deep .dropdown-toggle::after {
				display: none !important;
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CareerOperationsCellRendererComponent implements ICellRendererAngularComp {
	private params!: CareerOperationsParams;

	private allButtons: OpButton[] = [
		{ key: 'career', icon: 'external-link-alt', title: 'common_btn_go_to_career', className: 'btn-outline-secondary', visible: () => true },

		{ key: 'shifts', icon: 'calendar-alt', title: 'common_action_shifts', className: 'btn-outline-secondary', visible: () => true },
		{ key: 'rotations', icon: 'sync-alt', title: 'common_action_rotations', className: 'btn-outline-secondary', visible: () => true },
		{
			key: 'suspensions',
			icon: 'pause-circle',
			title: 'common_action_suspensions',
			className: 'btn-outline-secondary',
			visible: () => true,
		},
		{ key: 'objectives', icon: 'bullseye', title: 'common_action_objectives', className: 'btn-outline-secondary', visible: () => true },
		{
			key: 'registrations',
			icon: 'list-alt',
			title: 'common_action_registrations',
			className: 'btn-outline-secondary',
			visible: () => true,
		},
		{ key: 'report', icon: 'file-alt', title: 'common_action_report', className: 'btn-outline-secondary', visible: () => true },
		{
			key: 'generate_ds',
			icon: 'cog',
			title: 'common_action_generate_diploma_supplement',
			className: 'btn-outline-primary',
			visible: c => !c?.diploma_supplement_file?.id && !c?.is_generating_diploma_supplement,
		},
		{
			key: 'download_ds',
			icon: 'file-pdf',
			title: 'common_action_download_diploma_supplement',
			className: 'btn-outline-success',
			visible: c => !!c?.diploma_supplement_file?.id,
		},
		{
			key: 'generate_allegato',
			icon: 'cogs',
			title: 'common_action_generate_ds_attachment',
			className: 'btn-outline-primary',
			visible: c => !c?.diploma_supplement_allegato_file?.id && !c?.is_generating_allegato,
		},
		{
			key: 'download_allegato',
			icon: 'file-pdf',
			title: 'common_action_download_ds_attachment',
			className: 'btn-outline-success',
			visible: c => !!c?.diploma_supplement_allegato_file?.id,
		},
		{ key: 'send_alert', icon: 'envelope', title: 'common_action_send_alert_email', className: 'btn-outline-warning', visible: () => true },
	];

	buttons: OpButton[] = [];

	get isGenerating(): boolean {
		return !!this.params?.data?.is_generating_diploma_supplement || !!this.params?.data?.is_generating_allegato;
	}

	agInit(params: CareerOperationsParams): void {
		this.params = params;
		const career = params.data;
		this.buttons = this.allButtons.filter(b => b.visible(career));
	}

	onClick(op: CareerOperationKey): void {
		if (this.params?.onOperation) {
			this.params.onOperation(op, this.params.data);
		}
	}

	refresh(params: ICellRendererParams): boolean {
		this.agInit(params as CareerOperationsParams);
		return true;
	}
}
